// ============================================================================
// CHANGO V5.0 — <ProductLookup> (Task #66 · D3 接入 QuoteWizard)
// ============================================================================
// A debounced typeahead combobox that surfaces the tenant's products catalog
// inside the QuoteWizard line-items table. Users can still manually type an
// arbitrary SKU string — the dropdown is purely suggestive (no forced pick).
//
// Behavior:
//   - Text input reflects `value` (the SKU string); typing updates it
//     through `onChange(skuString)` so manual-entry users are untouched.
//   - 200ms after the user stops typing, we GET /api/products?q=…&archived=0.
//   - Suggestions render beneath the field. Keyboard (↑ ↓ Enter Esc) and
//     mouse both select. Selection triggers `onSelect(product)` — the parent
//     decides what to autofill (sku, description, unit_sell_rmb, unit_cost_rmb).
//   - Input is rendered with a wrapping <div class="relative"> so the
//     absolutely-positioned dropdown doesn't distort the surrounding table.
//
// Role assumptions:
//   QuoteWizard is boss|finance-only (sales is 403'd out at the route gate),
//   so we DO pass cost through when it's present on the product row. The
//   /api/products backend already projects default_cost_rmb only for non-sales.
//   Defense in depth: we check `product.default_cost_rmb != null` before
//   surfacing it, so if the backend ever tightens further it still degrades
//   to "price-only autofill" rather than writing `unit_cost_rmb: undefined`.
//
// Not reached for by design:
//   - No "force-pick" mode. If UEDA later wants to forbid ad-hoc SKUs, we'd
//     add a `strict` prop here + validation at the wizard level.
//   - No product_id FK persistence on the quote line. See build log for
//     Task #66 for the deferral rationale — SKU string stays the reference.
// ============================================================================

import React, { useEffect, useRef, useState } from 'react';

import { api } from '../api/client.js';

/**
 * @param {object}   props
 * @param {string}   props.value      — current SKU text
 * @param {function} props.onChange   — (skuString) => void, for manual edit
 * @param {function} props.onSelect   — (product)   => void, autofill trigger
 * @param {string}  [props.placeholder]
 * @param {boolean} [props.disabled]
 */
export default function ProductLookup({
  value,
  onChange,
  onSelect,
  placeholder = 'SKU-001',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced fetch whenever the value changes AND we're "open".
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        // Guard: empty query still returns recent items (limit=20).
        const params = new URLSearchParams({ limit: '20', archived: '0' });
        const q = (value || '').trim();
        if (q.length > 0) params.set('q', q);
        const res = await api.get(`/api/products?${params.toString()}`);
        const list = Array.isArray(res?.items) ? res.items : [];
        setItems(list);
        setHighlight(list.length > 0 ? 0 : -1);
      } catch (e) {
        setError(e?.message || '读取产品目录失败');
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, open]);

  // Click-outside closes the dropdown. We also reset highlight so the next
  // open starts clean — otherwise a stale index can point past the list.
  useEffect(() => {
    function onDocClick(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
        setHighlight(-1);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function handleSelect(product) {
    setOpen(false);
    setHighlight(-1);
    onSelect && onSelect(product);
  }

  function handleKey(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < items.length) {
        // Only swallow Enter when a real suggestion is targeted. This lets
        // the wizard's form-submit key flow through when the dropdown is
        // open but empty / in a "no match" state.
        e.preventDefault();
        handleSelect(items[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        className="field"
        value={value || ''}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={80}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-haspopup="listbox"
        onChange={(e) => {
          onChange && onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
      />

      {open && (
        <div
          role="listbox"
          className="absolute left-0 right-0 z-30 mt-1 max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-400">加载中…</div>
          )}
          {error && (
            <div className="px-3 py-2 text-xs text-rose-600">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">
              （没有匹配的产品。直接输入 SKU 即可手动录入。）
            </div>
          )}
          {!loading && !error && items.map((p, i) => {
            const active = i === highlight;
            const hasCost = p.default_cost_rmb != null;
            return (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={active}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  // onMouseDown (not onClick) so the input doesn't blur
                  // and close the dropdown before the handler fires.
                  e.preventDefault();
                  handleSelect(p);
                }}
                className={[
                  'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm',
                  active ? 'bg-chango-50 text-chango-800' : 'hover:bg-slate-50',
                ].join(' ')}
              >
                <span className="flex flex-col min-w-0">
                  <span className="truncate font-medium">{p.sku_code}</span>
                  <span className="truncate text-xs text-slate-500">
                    {p.name || '(无名称)'}
                    {p.unit ? ` · ${p.unit}` : ''}
                  </span>
                </span>
                <span className="flex flex-col items-end shrink-0 text-xs tabular-nums">
                  <span className="text-slate-700">
                    售 ¥{Number(p.default_price_rmb ?? 0).toFixed(2)}
                  </span>
                  {hasCost ? (
                    <span className="text-slate-400">
                      成本 ¥{Number(p.default_cost_rmb).toFixed(2)}
                    </span>
                  ) : (
                    <span className="text-slate-300">无成本</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
